// grid debug call <to-entity> <path> — reworked for Decision 051.
//
// Developer debug helper: resolve an entity's PUBLIC serving URL from an
// address (app slug, <grid-slug>/<app-slug>, or entity id) and fire a plain
// HTTP request at it. Useful for poking a public endpoint without hunting
// for the URL.
//
// Under Decision 051 app-to-app calls carry no tokens — a per-callee
// NetworkPolicy enforces the declared `callers:` allow-list in-cluster, and
// entities call each other via `runtime.call` on the internal URL, which is
// unreachable from a laptop. So this helper is public-only by design: it
// sends no caller identity and exercises none of the grant machinery. Grant
// management lives in `grid callers` / `grid calls`.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { CloudGridError, isVerbose } from '../utils.js';
import { handleV2Error } from '../handle-error.js';
import {
  parseCallTarget,
  lookupCallEntity,
} from './caller-grants/shared.js';

// ── Outbound HTTP execution ───────────────────────────────────────────────

export interface CallRequestInput {
  /** Target entity's serving URL (no trailing slash, no path). */
  url: string;
  /** Path component the user supplied (must start with /). */
  path: string;
  method: string;
  body: string | undefined;
  /** Raw -H values: 'Key: Value'. */
  headers: string[];
}

export interface CallResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
}

export type FetchImpl = (url: string, init: RequestInit) => Promise<Response>;

/**
 * Compose the full outbound URL from an entity's serving URL and a
 * user-supplied path. Strips a trailing slash from the URL and ensures the
 * path starts with one.
 */
function composeOutboundUrl(servingUrl: string, path: string): string {
  return `${servingUrl.replace(/\/+$/, '')}${path.startsWith('/') ? path : '/' + path}`;
}

/**
 * Derive the request headers from the raw `-H` values plus a content-type
 * guess for `-d` bodies. Shared by the actual send and the verbose preview
 * so the printed headers are what was attached.
 */
function deriveHeaders(rawHeaders: string[], body: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const h of rawHeaders) {
    const idx = h.indexOf(':');
    if (idx === -1) {
      throw new CloudGridError(
        `Header ${JSON.stringify(h)} is malformed. Use -H 'Key: Value'.`,
      );
    }
    headers[h.slice(0, idx).trim()] = h.slice(idx + 1).trim();
  }
  if (body !== undefined) {
    try {
      JSON.parse(body);
      headers['Content-Type'] ??= 'application/json';
    } catch {
      headers['Content-Type'] ??= 'text/plain';
    }
  }
  return headers;
}

/**
 * Send the request. Every response — 2xx or not — is returned unchanged so
 * the developer can inspect their app's behavior.
 */
export async function sendCallerRequest(
  input: CallRequestInput,
  fetchImpl: FetchImpl = (url, init) => fetch(url, init),
): Promise<CallResponse> {
  const fullUrl = composeOutboundUrl(input.url, input.path);
  const headers = deriveHeaders(input.headers, input.body);

  const res = await fetchImpl(fullUrl, {
    method: input.method,
    headers,
    body: input.body,
  });
  const text = await res.text();

  const respHeaders: Record<string, string> = {};
  res.headers.forEach((v, k) => { respHeaders[k] = v; });
  return { status: res.status, statusText: res.statusText, headers: respHeaders, body: text };
}

// ── Rendering ─────────────────────────────────────────────────────────────

function renderDefault(out: NodeJS.WriteStream, resp: CallResponse): void {
  out.write(`${resp.status} ${resp.statusText}\n`);
  out.write(resp.body);
  if (!resp.body.endsWith('\n')) out.write('\n');
}

function renderJson(out: NodeJS.WriteStream, resp: CallResponse): void {
  out.write(JSON.stringify({
    status: resp.status,
    headers: resp.headers,
    body: resp.body,
  }) + '\n');
}

function renderVerbose(
  out: NodeJS.WriteStream,
  resp: CallResponse,
  reqUrl: string,
  reqHeaders: Record<string, string>,
): void {
  out.write(`Request: ${reqUrl}\n`);
  for (const [k, v] of Object.entries(reqHeaders)) {
    out.write(`  ${k}: ${v}\n`);
  }
  out.write(`\nResponse: ${resp.status} ${resp.statusText}\n`);
  for (const [k, v] of Object.entries(resp.headers)) {
    out.write(`  ${k}: ${v}\n`);
  }
  out.write(`\n${resp.body}`);
  if (!resp.body.endsWith('\n')) out.write('\n');
}

// ── Command implementation ────────────────────────────────────────────────

export interface CallOpts {
  method?: string;
  data?: string;
  header?: string[];
  verbose?: boolean;
  json?: boolean;
}

export async function runCall(
  toAddr: string,
  path: string,
  opts: CallOpts,
  client?: IV2Client,
  fetchImpl?: FetchImpl,
): Promise<void> {
  const apiClient = client ?? (await getV2Client());

  const target = parseCallTarget(toAddr);
  const targetDetail = await lookupCallEntity(apiClient, target);

  const headers = opts.header ?? [];
  const method = (opts.method ?? 'GET').toUpperCase();
  const reqUrl = composeOutboundUrl(targetDetail.url, path);

  const resp = await sendCallerRequest({
    url: targetDetail.url,
    path,
    method,
    body: opts.data,
    headers,
  }, fetchImpl);

  // Either the per-command `--verbose` flag or the root `-v/--verbose`
  // flag should trigger verbose output. The root flag is set by index.ts's
  // preAction hook before the command's action runs, so isVerbose() is
  // authoritative there.
  const verbose = !!opts.verbose || isVerbose();

  if (opts.json) {
    renderJson(process.stdout, resp);
  } else if (verbose) {
    renderVerbose(process.stdout, resp, reqUrl, deriveHeaders(headers, opts.data));
  } else {
    renderDefault(process.stdout, resp);
  }
}

// ── Commander command export ──────────────────────────────────────────────

export const callCommand = new Command('call')
  .description("Send a test request to an entity's public URL")
  .argument('<to-entity>', 'Target address (e.g. billing-service or qa-todo/todo-api)')
  .argument('<path>', 'HTTP path on the target (e.g. /api/health)')
  .option('-X, --method <method>', 'HTTP method', 'GET')
  .option('-d, --data <body>', 'Request body (sent as-is)')
  .option('-H, --header <header...>', 'Additional request headers (Key: Value)')
  .option('--verbose', 'Show full request and response headers')
  .option('--json', 'Machine-readable JSON output')
  .action(async (toEntity: string, path: string, opts: CallOpts) => {
    try {
      await runCall(toEntity, path, opts);
    } catch (err) {
      if (err instanceof CloudGridError) {
        process.stderr.write(`Error: ${err.message}\n`);
        process.exit(1);
      }
      await handleV2Error(err, { json: !!opts.json });
    }
  });
